/**
 * Runs grouped by the scenario they belong to.
 *
 * A flat list stopped making sense once a scenario became a workspace: eight
 * entries in a row are eight iterations of one line of thinking, and reading
 * them as unrelated events loses the only structure that matters. Grouping
 * restores it, and numbering within a group says which iteration you are
 * looking at.
 */
export interface RunLike {
  runId: string;
  createdAt: string;
  scenarioId: string | null;
  scenarioName: string | null;
  scenarioStatus?: string | null;
  solverStatus: string;
  churn: number | null;
  wallTimeS: number | null;
}

export interface RunGroup<T extends RunLike> {
  scenarioId: string | null;
  scenarioName: string | null;
  status: string | null;
  /** Newest first, as the group is read; each carries its iteration number. */
  runs: (T & { iteration: number })[];
  latestAt: string;
  solves: number;
  /** Iterations that produced no schedule — the ones worth looking at. */
  failures: number;
  totalMoved: number;
}

export function groupRunsByScenario<T extends RunLike>(runs: T[]): RunGroup<T>[] {
  const byScenario = new Map<string, T[]>();
  for (const r of runs) {
    // Runs from before scenarios were tracked share one bucket rather than
    // becoming one group each, which would be noise shaped like structure.
    const key = r.scenarioId ?? "__none__";
    const list = byScenario.get(key) ?? [];
    list.push(r);
    byScenario.set(key, list);
  }

  const groups: RunGroup<T>[] = [];
  for (const [key, list] of byScenario) {
    // Number by when they happened, then present newest first: you want to
    // see the latest, and still know it was the sixth thing you tried.
    const ascending = [...list].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    const numbered = ascending.map((r, i) => ({ ...r, iteration: i + 1 }));
    const newestFirst = [...numbered].reverse();
    const named = list.find((r) => r.scenarioName)?.scenarioName ?? null;

    groups.push({
      scenarioId: key === "__none__" ? null : key,
      scenarioName: named,
      status: list.find((r) => r.scenarioStatus)?.scenarioStatus ?? null,
      runs: newestFirst,
      latestAt: newestFirst[0]!.createdAt,
      solves: list.length,
      failures: list.filter((r) => r.solverStatus === "INFEASIBLE" || r.solverStatus === "ERROR").length,
      totalMoved: list.reduce((n, r) => n + (r.churn ?? 0), 0),
    });
  }

  return groups.sort((a, b) => b.latestAt.localeCompare(a.latestAt));
}
